import {useContext, createContext, useMemo, useCallback, useState, useEffect} from 'react';
import {type User, getAuth, onAuthStateChanged} from 'firebase/auth';
import {useHistory, useLocation} from 'react-router-dom';
import qs from 'query-string';
import {sendSignInSlackAlert} from 'slack/events';

import {User as RenditionUser} from 'rendition-core/types/Rendition';

import {createUserIfNotFound, getUser, signOutWithGoogle, signInWithGoogle} from '../../firebase_utils';
import {useTimeoutEffect} from '@react-hookz/web';
import axios from 'axios';
import {useAppStore} from 'stores/app';
import {getUser as getBackendUser} from 'Common/api';

export const SIGNED_OUT = 'SIGNED_OUT';
export const SIGNED_IN_PAID = 'SIGNED_IN_PAID';
export const SIGNED_IN_UNPAID = 'SIGNED_IN_UNPAID';
export const USER_LOADING = 'USER_LOADING';

export const AuthState = {
    SIGNED_OUT: SIGNED_OUT,
    SIGNED_IN_PAID: SIGNED_IN_PAID,
    SIGNED_IN_UNPAID: SIGNED_IN_UNPAID,
    USER_LOADING: USER_LOADING,
} as const;

export const getShowUpgrade = (user: RenditionUser, availableCredits: number) => {
    return !user.paid && availableCredits <= 1;
};

export type AuthStatus = (typeof AuthState)[keyof typeof AuthState];

export interface UserState {
    status: AuthStatus;
    userId: string;
    user?: RenditionUser;
    setUser: React.Dispatch<React.SetStateAction<RenditionUser | undefined>>;
    deviceId: string;
    sessionId: string;
    signIn: () => Promise<void>;
    signOut: () => Promise<void>;
    getUserDefaultProject: () => Promise<any>;
    availableCredits: number;
}

const UserContext = createContext<UserState>({
    status: AuthState.SIGNED_OUT,
    userId: '',
    deviceId: '',
    sessionId: '',
    setUser: () => {},
    signIn: async () => {},
    signOut: async () => {},
    getUserDefaultProject: async () => '',
    availableCredits: 0,
});

export const UserContextProvider: React.FC<React.PropsWithChildren> = ({children}) => {
    const [status, setStatus] = useState<AuthStatus>(AuthState.USER_LOADING);
    const [user, setUser] = useState<RenditionUser | undefined>();
    const [userId, setUserId] = useState<string>('');
    const [availableCredits, setAvilableCredits] = useState<number>(0);
    const setToast = useAppStore.use.setToast();

    const history = useHistory();
    const location = useLocation();
    const parsed = qs.parse(location?.search);
    const {deviceId, sessionId, redirectUrl} = parsed as {
        deviceId: string;
        sessionId: string;
        redirectUrl?: string;
    };

    const setSignedOutIfNoUserLoaded = useCallback(() => {
        setStatus(AuthState.SIGNED_OUT);
    }, []);

    const [cancelSetSignOut] = useTimeoutEffect(setSignedOutIfNoUserLoaded, 2500);

    const attemptSignIn = useCallback(
        async (gUser: User) => {
            setUserId(gUser ? gUser.uid : '');
            const userSnapshot = await getUser(gUser.uid);
            const user = userSnapshot.exists() ? (userSnapshot.val() as RenditionUser) : undefined;
            setUser(user);

            sendSignInSlackAlert(sessionId, gUser.uid, gUser);
            if (user) {
                if (user.paid) {
                    setStatus(AuthState.SIGNED_IN_PAID);
                } else {
                    setStatus(AuthState.SIGNED_IN_UNPAID);
                }

                if (gUser && user && redirectUrl) {
                    history.push(decodeURIComponent(redirectUrl));
                }

                const {availableCredits} = await getBackendUser({
                    userId: gUser.uid,
                });
                setAvilableCredits(availableCredits);
                return user;
            } else {
                // There's an edge case where if a user is deleted, firebase/google still thinks
                // that the user is logged in but it doesn't exist on the backend so we want to
                // just sign them out
                signOutWithGoogle();
            }
        },
        [sessionId, redirectUrl, history]
    );

    useEffect(() => {
        const auth = getAuth();
        const unSub = onAuthStateChanged(auth, async (user) => {
            cancelSetSignOut();
            if (user) {
                await createUserIfNotFound(sessionId, user);
                attemptSignIn(user);
            } else {
                setStatus(AuthState.SIGNED_OUT);
            }
        });
        return () => {
            unSub();
        };
    }, [sessionId, attemptSignIn, cancelSetSignOut]);

    const signIn = useCallback(async () => {
        setStatus(AuthState.USER_LOADING);
        await signInWithGoogle(sessionId); // creates session, gets or creates gUser

        setToast({
            type: 'SUCCESS',
            message: 'Sign in Successful! Head back to Figma to start using Rendition!',
            open: true,
        });
    }, [sessionId, setToast]);

    const signOut = useCallback(async () => {
        setStatus(AuthState.USER_LOADING);
        signOutWithGoogle();
        setStatus(AuthState.SIGNED_OUT);
        setUser(undefined);
    }, []);

    const getUserDefaultProject = useCallback(async () => {
        const body = {
            user: {
                userId,
            },
        };
        return axios.post(`/project/default-project`, body);
    }, [userId]);

    const value = useMemo(
        () => ({
            status,
            userId,
            user,
            setUser,
            deviceId,
            sessionId,
            signIn,
            signOut,
            getUserDefaultProject,
            availableCredits,
        }),
        [status, deviceId, sessionId, userId, signIn, signOut, user, getUserDefaultProject, availableCredits]
    );

    return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUserContext = () => {
    return useContext(UserContext);
};
